import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Target, Lightbulb, TrendingUp, BadgeCheck } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import useSeo from '../hooks/useSeo';
import CaseStudyCard, { BrandLogo } from '../components/CaseStudyCard';
import CTA from '../components/CTA';
import { caseStudies } from '../mock/mock';

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const cs = caseStudies.find((c) => c.slug === slug);
  useReveal(slug);
  useSeo({ title: cs ? `${cs.brand} Case Study | Interscope Ads` : 'Case Study | Interscope Ads', description: cs ? cs.tagline : 'Interscope performance marketing case study.' });

  if (!cs) {
    return (
      <main className="pt-40 pb-24 text-center">
        <h1 className="font-display text-3xl font-bold text-slate-900">Case study not found</h1>
        <Link to="/case-studies" className="mt-6 inline-flex items-center gap-2 text-[#2563eb]">
          <ArrowLeft size={16} /> Back to case studies
        </Link>
      </main>
    );
  }

  const others = caseStudies.filter((c) => c.slug !== slug).slice(0, 3);
  const blocks = [
    { icon: Target, title: 'The Challenge', body: cs.challenge },
    { icon: Lightbulb, title: 'Our Solution', body: cs.solution },
    { icon: TrendingUp, title: 'The Results', body: cs.results },
  ];
  const detailRows = [
    ['Industry', cs.industry],
    ['Target Market', cs.market],
    ['Objective', cs.objective],
    ['Campaign Type', cs.campaignType],
    ['Installs', cs.installs],
    ['CPA / CPI', cs.cpi],
    ['Conversion Rate', cs.conversionRate],
    ['ROAS', cs.roas],
    ['Key Results', cs.keyResults],
  ].filter(([, v]) => v && v !== '\u2014');

  return (
    <main className="pt-32">
      <section className="relative max-w-5xl mx-auto px-5 md:px-8">
        <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#2563eb] transition-colors mb-8">
          <ArrowLeft size={16} /> All case studies
        </Link>
        <div className="flex items-center gap-4 mb-4">
          <BrandLogo cs={cs} size="lg" />
          <div>
            <span className="inline-block mb-1 rounded-full bg-[#2563eb]/12 px-3 py-1 text-xs font-medium text-[#2563eb] border border-[#2563eb]/30">{cs.category}</span>
            {cs.verified && <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><BadgeCheck size={13} /> {cs.verified}</span>}
          </div>
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-bold text-slate-900 leading-[1.05]">{cs.brand}</h1>
        <p className="mt-4 text-xl text-slate-600">{cs.tagline}</p>
        {cs.date && (
          <p className="mt-2 text-sm font-medium text-slate-500" data-testid="case-study-date">Updated {cs.date}</p>
        )}

        {cs.image && (
          <div className="relative mt-10 rounded-3xl overflow-hidden border border-slate-200">
            <img src={cs.image} alt={cs.brand} className="w-full h-[300px] md:h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b12]/70 to-transparent" />
          </div>
        )}

        {cs.gallery && (
          <div className="mt-4 grid gap-4">
            {cs.gallery.map((src, i) => (
              <div key={i} className="rounded-3xl overflow-hidden border border-slate-200 bg-slate-50" data-testid={`case-study-gallery-${i}`}>
                <img src={src} alt={`${cs.brand} creative ${i + 1}`} className="w-full max-h-[420px] object-contain" />
              </div>
            ))}
          </div>
        )}

        {/* Structured fields */}
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {detailRows.map(([k, v]) => (
            <div key={k} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#2563eb]">{k}</div>
              <div className="mt-1 text-sm text-slate-700 leading-snug">{v}</div>
            </div>
          ))}
        </div>


        {/* Meta bar */}
        {cs.meta && (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(cs.meta).map(([k, v]) => (
              <div key={k} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-[#2563eb]">{k}</div>
                <div className="mt-1 text-sm text-slate-700 leading-snug">{v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-3 gap-4 mt-8">
          {cs.metrics.map((m) => (
            <div key={m.label} className="glass rounded-2xl p-6 text-center">
              <div className="font-display text-2xl md:text-4xl font-bold text-gradient-cyan">{m.value}</div>
              <div className="mt-1.5 text-xs md:text-sm text-slate-500">{m.label}</div>
            </div>
          ))}
        </div>

        {/* Extra metrics */}
        {cs.extraMetrics && (
          <div className="grid grid-cols-3 gap-4 mt-4">
            {cs.extraMetrics.map((m) => (
              <div key={m.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-center">
                <div className="font-display text-xl md:text-2xl font-bold text-slate-900">{m.value}</div>
                <div className="mt-1 text-[11px] md:text-xs text-slate-500">{m.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Market breakdown */}
        {cs.breakdown && (
          <div className="mt-10 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="case-study-breakdown">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="font-display text-lg font-semibold text-slate-900">Results by market</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                    {cs.breakdown.columns.map((c) => (
                      <th key={c} className="px-5 py-3 font-semibold whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cs.breakdown.rows.map((row, ri) => {
                    const isTotal = ri === cs.breakdown.rows.length - 1;
                    return (
                      <tr key={ri} className={`border-b border-slate-100 last:border-0 ${isTotal ? 'bg-blue-50/60 font-semibold text-slate-900' : 'text-slate-600'}`}>
                        {row.map((cell, ci) => (
                          <td key={ci} className={`px-5 py-3 whitespace-nowrap tabular-nums ${ci === 0 ? 'font-medium text-slate-900' : ''}`}>{cell}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Story */}
        <div className="mt-12 space-y-6">
          {blocks.map((b) => (
            <div key={b.title} className="glass rounded-2xl p-7 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#2563eb]/12 grid place-items-center text-[#2563eb]">
                  <b.icon size={20} />
                </div>
                <h2 className="font-display text-xl font-semibold text-slate-900">{b.title}</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Related */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 section-pad">
        <div className="flex items-center justify-between mb-10">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900">More case studies</h2>
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm font-semibold text-[#2563eb]">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {others.map((o) => (
            <CaseStudyCard key={o.slug} cs={o} />
          ))}
        </div>
      </section>

      <CTA />
    </main>
  );
}
